import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { formFieldLabels, formShapeOf } from '@lenderhound/shared';
import {
  metrics as fetchMetrics, type FunnelCounts, type Metrics as MetricsData, type Traffic,
} from '../api.ts';

/**
 * The metrics report.
 *
 * Henry Liner, 2026-09-16: "are we tracking view ae contact button ... This will be useful
 * metrics for when I talk to banks ... so at any time we can run a report like: How many
 * view Ae contact clicks did Logan get in September".
 *
 * So the clicks table is the screen and everything else is context around it. One row per
 * lender, one column per month, biggest total first, and the answer to his sentence is a
 * cell. The tiles above it are there to say how much traffic those clicks came out of -
 * seven clicks in a month of forty searches and seven in a month of four hundred are not
 * the same fact, and a number read to a bank without its denominator is a number that gets
 * corrected in the room.
 *
 * NO CHART. A bank meeting wants figures that can be quoted, and every column here is
 * already a small integer. A chart would round them and add a library.
 */

/** One lender's row: its months, its total, its denominator and the rate between them. */
export interface ClickRow {
  readonly slug: string;
  readonly name: string;
  /** Clicks keyed by YYYY-MM. A month with no clicks is absent, and renders as a dash. */
  readonly clicks: Readonly<Record<string, number>>;
  readonly total: number;
  /** Answers over the window in which this lender could do the loan. See `eligible` below. */
  readonly eligible: number;
  /** total / eligible, or null when the lender was never eligible - never NaN or Infinity. */
  readonly rate: number | null;
}

/**
 * Fold the report into the table it is read as.
 *
 * Exported and pure so it can be tested without a DOM. Every lender that was clicked OR was
 * eligible gets a row: a lender shown a hundred times and rung zero times is the strongest
 * signal on this page, and dropping it because its click column is empty would delete
 * exactly the fact Henry is taking into the meeting.
 */
export function clickRows(data: MetricsData): ClickRow[] {
  const byLender = new Map<string, { name: string; clicks: Record<string, number>;
                                     total: number; eligible: number }>();
  const row = (slug: string, name: string) => {
    let r = byLender.get(slug);
    if (!r) { r = { name, clicks: {}, total: 0, eligible: 0 }; byLender.set(slug, r); }
    return r;
  };
  for (const c of data.aeClicks.byMonth) {
    const r = row(c.slug, c.name);
    r.clicks[c.month] = (r.clicks[c.month] ?? 0) + c.clicks;
  }
  // The total comes off the server's own total rows rather than being re-added here. They
  // are one SQL scan with the months, so taking them as given is what keeps the figure in
  // the Total column identical to the one the API would report on its own.
  for (const t of data.aeClicks.totals) row(t.slug, t.name).total = t.clicks;
  for (const s of data.lendersShown) row(s.slug, s.name).eligible += s.eligible;

  return [...byLender.entries()]
    .map(([slug, r]) => ({
      slug, name: r.name, clicks: r.clicks, total: r.total, eligible: r.eligible,
      rate: r.eligible > 0 ? r.total / r.eligible : null,
    }))
    .sort((a, b) => b.total - a.total || b.eligible - a.eligible || a.name.localeCompare(b.name));
}

/** A field, quoted only when it has to be. Lender names carry commas - "NMSI Inc." does. */
function csvCell(value: string | number): string {
  const s = String(value);
  return /[",\n\r]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
}

/**
 * The clicks table as a spreadsheet, built in the browser from what is already on screen.
 *
 * Exported and pure for the same reason `clickRows` is, and because the download itself -
 * a Blob and an anchor - is not something a test can assert anything useful about. What
 * matters is that the file says the same thing the table does.
 *
 * The month columns are raw YYYY-MM rather than "Sep 2026": this lands in a spreadsheet,
 * where a sortable key beats a pretty one. The rate is a percentage to one decimal because
 * a ratio like 0.0107 in a column of integers gets read as a count.
 */
export function clicksCsv(months: readonly string[], rows: readonly ClickRow[]): string {
  const header = ['Lender', 'Slug', ...months, 'Total clicks', 'Times eligible', 'Click rate %'];
  const lines = [header.map(csvCell).join(',')];
  for (const r of rows) {
    lines.push([
      csvCell(r.name), csvCell(r.slug),
      ...months.map((m) => csvCell(r.clicks[m] ?? 0)),
      csvCell(r.total), csvCell(r.eligible),
      csvCell(r.rate === null ? '' : (r.rate * 100).toFixed(1)),
    ].join(','));
  }
  // A trailing newline: a CSV without one is a file whose last row some tools drop.
  return `${lines.join('\n')}\n`;
}

/**
 * "2026-09" as "Sep 2026", without ever handing a partial date to `new Date(string)`.
 *
 * `new Date('2026-09')` is parsed as UTC midnight and then rendered in the local zone, so
 * west of Greenwich it prints August. Building the date from its parts and formatting it
 * back in UTC keeps the label on the month it names. An unparseable value prints as itself
 * rather than as "Invalid Date" - the same rule Admin.tsx applies to ingest dates.
 */
export function monthLabel(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
    .format(date);
}

/** One step of the funnel as it is read: its name, its browsers, and its share of visitors. */
export interface FunnelStep {
  readonly label: string;
  readonly count: number;
  /** count / visitors, or null when there were no visitors - never NaN. */
  readonly ofVisitors: number | null;
}

/**
 * The five steps in the order a broker takes them, with the share of visitors each one kept.
 *
 * Exported and pure so the arithmetic is tested without a DOM. The share is always of
 * VISITORS rather than of the step above, because "5 of 100" is the sentence Henry asked for
 * and a chain of step-to-step rates multiplies into a number nobody can check by eye.
 */
export function funnelSteps(c: FunnelCounts): FunnelStep[] {
  const of = (n: number) => (c.visitors > 0 ? n / c.visitors : null);
  return [
    { label: 'Visited', count: c.visitors, ofVisitors: of(c.visitors) },
    { label: 'Started the form', count: c.formStarted, ofVisitors: of(c.formStarted) },
    { label: 'Search blocked - form incomplete', count: c.searchBlocked,
      ofVisitors: of(c.searchBlocked) },
    { label: 'Pressed Search', count: c.searched, ofVisitors: of(c.searched) },
    { label: 'Asked for a code', count: c.codeRequested, ofVisitors: of(c.codeRequested) },
    { label: 'Verified their phone', count: c.verified, ofVisitors: of(c.verified) },
    { label: 'Got an answer', count: c.answered, ofVisitors: of(c.answered) },
  ];
}

/**
 * "2026-09-17" as "Thu, Sep 17", by the same rule monthLabel follows: never hand a partial
 * date string to `new Date`, which reads it as UTC and prints the day before west of London.
 */
export function dayLabel(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  }).format(date);
}

const FIELD_LABELS: Readonly<Record<string, string>> = formFieldLabels(formShapeOf({}));

/** A form key as the form asks it, without its number; an unknown key prints as itself. */
export function fieldLabel(key: string): string {
  if (key === 'ltv') return 'LTV %';
  if (key === 'funkyText') return "What's funky about this file?";
  return (FIELD_LABELS[key] ?? key).replace(/^\d+\.\s*/, '');
}

const pct = (rate: number | null): string =>
  rate === null ? '—' : `${(rate * 100).toFixed(1)}%`;

const num = (n: number): string => n.toLocaleString();

/** The window choices. Six is the default because it is the one Henry's question spans. */
const WINDOWS = [1, 3, 6, 12] as const;

const numCell = {
  padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
} as const;
const numHead = { padding: '6px 8px', textAlign: 'right' } as const;

export default function Metrics({ onClose }: { onClose: () => void }) {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState<MetricsData | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);

  const load = useCallback(async (window_: number) => {
    setBusy(true); setError('');
    try {
      setData(await fetchMetrics(window_));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the report.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { void load(months); }, [load, months]);

  const rows = useMemo(() => (data ? clickRows(data) : []), [data]);

  const download = (): void => {
    if (!data) return;
    const csv = clicksCsv(data.months, rows);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `lenderhound-ae-clicks-${data.window.from}-to-${data.window.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // The last month in the window is the current one - the server builds the list that way -
  // and it is what the tiles report. Named rather than indexed inline so the three tiles
  // cannot drift onto different months.
  const current = data?.months[data.months.length - 1] ?? null;
  const thisMonth = {
    searches: data?.searches.find((s) => s.month === current) ?? null,
    brokers: data?.brokers.find((b) => b.month === current) ?? null,
    chat: data?.chat.find((c) => c.month === current) ?? null,
    traffic: data?.traffic.byMonth.find((t) => t.month === current) ?? null,
  };
  const windowTotals = {
    searches: (data?.searches ?? []).reduce((n, s) => n + s.inquiries, 0),
    registered: (data?.brokers ?? []).reduce((n, b) => n + b.registered, 0),
    turns: (data?.chat ?? []).reduce((n, c) => n + c.turns, 0),
  };

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', rowGap: 10, marginBottom: 18,
      }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {WINDOWS.map((w) => (
            <button key={w} type="button"
                    className={`btn ${months === w ? '' : 'btn-ghost'}`}
                    aria-pressed={months === w}
                    onClick={() => setMonths(w)}>
              {w === 1 ? 'This month' : `Last ${w} months`}
            </button>
          ))}
        </div>
        <button className="btn btn-ghost" onClick={onClose}>Back to search</button>
      </div>

      {error ? (
        <div className="card" style={{ borderColor: 'var(--red)', color: 'var(--red)', marginBottom: 16 }}>
          {error}
        </div>
      ) : null}

      {busy && !data ? <div className="card">Loading…</div> : null}

      {data ? (
        <>
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="lbl" style={{ marginBottom: 10 }}>
              {current ? monthLabel(current) : 'This month'} so far
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28 }}>
              <Tile label="Visitors" value={thisMonth.traffic?.visitors ?? 0}
                    sub={thisMonth.traffic
                      ? `${num(thisMonth.traffic.answered)} got an answer`
                      : ''} />
              <Tile label="Searches" value={thisMonth.searches?.inquiries ?? 0}
                    sub={thisMonth.searches
                      ? `${num(thisMonth.searches.paid)} paid · `
                        + `${num(thisMonth.searches.cached)} repeat`
                      : ''} />
              <Tile label="Brokers registered" value={thisMonth.brokers?.registered ?? 0}
                    sub={thisMonth.brokers
                      ? `${num(thisMonth.brokers.searchers)} account${
                        thisMonth.brokers.searchers === 1 ? '' : 's'} searched`
                      : ''} />
              <Tile label="Follow-up questions" value={thisMonth.chat?.turns ?? 0}
                    sub={`${num(windowTotals.turns)} over the window`} />
            </div>
            <div style={{
              borderTop: '1px solid var(--cream-line)', marginTop: 14, paddingTop: 12,
              fontSize: 12, color: 'var(--muted)',
            }}>
              Over {data.window.from} – {data.window.to}: {num(windowTotals.searches)} searches,
              {' '}{num(windowTotals.registered)} accounts created.
              {' '}All months are {data.window.timezone.replace('_', ' ')}.
            </div>
          </div>

          <TrafficCard traffic={data.traffic} from={data.window.from} to={data.window.to} />

          <div className="card">
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              flexWrap: 'wrap', rowGap: 8, marginBottom: 4,
            }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>“View AE Contact” clicks</h3>
              <button className="btn btn-ghost" type="button" onClick={download}
                      disabled={rows.length === 0}>
                Download CSV
              </button>
            </div>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--muted)' }}>
              Which lenders brokers actually ring, per month.{' '}
              <b>Eligible</b> is how many answers came back with that lender able to do the
              loan — the results page cards four lenders at a time, so a lender can be
              eligible without being one of the four. The rate is therefore a floor.
            </p>

            {rows.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                No lender was clicked or returned in this window.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                      <th style={{ padding: '6px 8px' }}>Lender</th>
                      {data.months.map((m) => (
                        <th key={m} style={numHead}>{monthLabel(m)}</th>
                      ))}
                      <th style={numHead}>Total</th>
                      <th style={numHead}>Eligible</th>
                      <th style={numHead}>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.slug} style={{ borderTop: '1px solid var(--cream-line)' }}>
                        <td style={{ padding: '8px' }}>
                          {r.name}
                          <span style={{ color: 'var(--faint)' }}> · {r.slug}</span>
                        </td>
                        {data.months.map((m) => (
                          <td key={m} style={numCell}>
                            {r.clicks[m] ? num(r.clicks[m]!)
                              : <span style={{ color: 'var(--faint)' }}>—</span>}
                          </td>
                        ))}
                        <td style={{ ...numCell, fontWeight: 600 }}>{num(r.total)}</td>
                        <td style={numCell}>{num(r.eligible)}</td>
                        <td style={numCell}>{pct(r.rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

/** One figure, dressed the way the admin console's health card dresses its counts. */
function Tile({ label, value, sub }: { label: string; value: number; sub: string }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontVariantNumeric: 'tabular-nums' }}>{num(value)}</div>
      <div className="lbl" style={{ marginBottom: 2 }}>{label}</div>
      {sub ? <div style={{ fontSize: 12, color: 'var(--muted)' }}>{sub}</div> : null}
    </div>
  );
}

const COLUMNS: readonly { key: keyof FunnelCounts; head: string }[] = [
  { key: 'visitors', head: 'Visitors' },
  { key: 'formStarted', head: 'Started' },
  { key: 'searchBlocked', head: 'Blocked' },
  { key: 'searched', head: 'Searched' },
  { key: 'codeRequested', head: 'Code' },
  { key: 'verified', head: 'Verified' },
  { key: 'answered', head: 'Answer' },
];

/**
 * A campaign's name as a marker, so a campaign row or a campaign-driven day is picked out
 * at a glance rather than read column by column.
 */
function CampaignTag({ name }: { name: string }) {
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: 12,
      background: 'var(--blue-soft)', color: 'var(--blue)', border: '1px solid var(--blue-line)',
      whiteSpace: 'nowrap',
    }}>
      {name}
    </span>
  );
}

type FunnelRowView = FunnelCounts & {
  key: string; label: ReactNode; campaignVisitors?: number; campaign?: boolean;
};

/**
 * Months, days or campaigns, one row each, the same five columns and the visit-to-answer
 * rate. `viaCampaign` adds how many of each row's visitors came in on a campaign link.
 */
function FunnelTable({ rows, viaCampaign = false }: {
  rows: readonly FunnelRowView[]; viaCampaign?: boolean;
}) {
  const dash = <span style={{ color: 'var(--faint)' }}>—</span>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
            <th style={{ padding: '6px 8px' }} />
            {COLUMNS.map((c) => (
              <Fragment key={c.key}>
                <th style={numHead}>{c.head}</th>
                {viaCampaign && c.key === 'visitors'
                  ? <th style={numHead}>Via campaign</th> : null}
              </Fragment>
            ))}
            <th style={numHead}>Visit → answer</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} style={{
              borderTop: '1px solid var(--cream-line)',
              background: r.campaign || (r.campaignVisitors ?? 0) > 0 ? 'var(--blue-soft)' : undefined,
            }}>
              <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{r.label}</td>
              {COLUMNS.map((c) => (
                <Fragment key={c.key}>
                  <td style={numCell}>{r[c.key] ? num(r[c.key]) : dash}</td>
                  {viaCampaign && c.key === 'visitors' ? (
                    <td style={{ ...numCell, color: 'var(--blue)', fontWeight: 600 }}>
                      {r.campaignVisitors ? num(r.campaignVisitors) : dash}
                    </td>
                  ) : null}
                </Fragment>
              ))}
              <td style={numCell}>{pct(r.visitors > 0 ? r.answered / r.visitors : null)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The By-campaign rows: each campaign marked, and everyone else as one "No campaign" row.
 * Exported and pure for the same reason funnelSteps is.
 */
export function campaignRows(byCampaign: Traffic['byCampaign']): FunnelRowView[] {
  return byCampaign.map((c) => ({
    ...c,
    key: c.campaign ?? '\u0000none',
    campaign: c.campaign !== null,
    label: c.campaign === null
      ? <span style={{ color: 'var(--muted)' }}>No campaign</span>
      : <CampaignTag name={c.campaign} />,
  }));
}

/**
 * Who came, and how far they got.
 *
 * Henry Liner, 2026-09-17: "it would be good to know that 100 people visited but only 5
 * queries". The funnel over the window is that sentence; the month and day tables are the
 * same five numbers over time, and the sources say where the hundred came from.
 *
 * Every figure is distinct browsers with our own taken out - see apps/api/src/traffic.ts -
 * and the page says both things in words, because a traffic number read aloud without them
 * gets asked about in the room.
 */
function TrafficCard({ traffic, from, to }: {
  traffic: Traffic; from: string | null; to: string | null;
}) {
  const steps = funnelSteps(traffic.window);
  const h4 = { margin: '18px 0 6px', fontSize: 14 } as const;
  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <h3 style={{ margin: 0, fontSize: 18 }}>Traffic</h3>
      <p style={{ margin: '4px 0 14px', fontSize: 13, color: 'var(--muted)' }}>
        {traffic.trackedSince
          ? <>Counting since <b>{dayLabel(traffic.trackedSince)}, {traffic.trackedSince.slice(0, 4)}</b>. </>
          : <>Nothing recorded yet. </>}
        Each number is a browser, not a page load, and any browser that has signed in to a
        staff or demo account is left out. Brokers already signed in skip the phone steps.
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 14, minWidth: 320 }}>
          <caption style={{
            textAlign: 'left', fontSize: 12, color: 'var(--muted)', paddingBottom: 6,
          }}>
            {from} – {to}
          </caption>
          <tbody>
            {steps.map((st) => (
              <tr key={st.label} style={{ borderTop: '1px solid var(--cream-line)' }}>
                <td style={{ padding: '8px 16px 8px 0' }}>{st.label}</td>
                <td style={{ ...numCell, fontWeight: 600 }}>{num(st.count)}</td>
                <td style={{ ...numCell, color: 'var(--muted)' }}>{pct(st.ofVisitors)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4 style={h4}>By campaign</h4>
      <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--muted)' }}>
        A visitor belongs to the first campaign link they arrived on, even if they came back
        later without it; everyone else is "No campaign". The rows add up to the totals above.
      </p>
      {traffic.byCampaign.length === 0
        ? <div style={{ fontSize: 13, color: 'var(--muted)' }}>No visitors in this window.</div>
        : <FunnelTable rows={campaignRows(traffic.byCampaign)} />}

      <h4 style={h4}>By month</h4>
      <FunnelTable rows={traffic.byMonth.map((m) => ({
        ...m, key: m.month, label: monthLabel(m.month),
      }))} />

      <h4 style={h4}>Last 14 days</h4>
      <FunnelTable viaCampaign rows={[...traffic.byDay].reverse().map((d) => ({
        ...d, key: d.day, label: dayLabel(d.day),
      }))} />

      <h4 style={h4}>What stopped Search</h4>
      <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--muted)' }}>
        Boxes the form flagged when Search was pressed with something missing or invalid,
        counted in browsers. "Blocked" above is how many browsers this happened to at least
        once; some fixed it and went on to search. Tracked since Oct 20, 2026.
      </p>
      {traffic.blockedFields.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>Nobody has been blocked in this window.</div>
      ) : (
        <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: 280 }}>
          <tbody>
            {traffic.blockedFields.map((b) => (
              <tr key={b.field} style={{ borderTop: '1px solid var(--cream-line)' }}>
                <td style={{ padding: '8px 16px 8px 0' }}>{fieldLabel(b.field)}</td>
                <td style={{ ...numCell, fontWeight: 600 }}>{num(b.visitors)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h4 style={h4}>Where they came from</h4>
      {traffic.sources.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>No visitors in this window.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                <th style={{ padding: '6px 8px' }}>Source</th>
                <th style={{ padding: '6px 8px' }}>Campaign</th>
                <th style={numHead}>Visitors</th>
                <th style={numHead}>Got an answer</th>
              </tr>
            </thead>
            <tbody>
              {traffic.sources.map((src) => (
                <tr key={`${src.source ?? ''}|${src.campaign ?? ''}`}
                    style={{ borderTop: '1px solid var(--cream-line)' }}>
                  <td style={{ padding: '8px' }}>
                    {src.source ?? <span style={{ color: 'var(--muted)' }}>Direct</span>}
                  </td>
                  <td style={{ padding: '8px' }}>
                    {src.campaign
                      ? <CampaignTag name={src.campaign} />
                      : <span style={{ color: 'var(--faint)' }}>—</span>}
                  </td>
                  <td style={numCell}>{num(src.visitors)}</td>
                  <td style={numCell}>{num(src.answered)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
